import React, { useEffect, useMemo, useRef } from "react"
import { select, mouse } from "d3-selection"
import _ from 'lodash';

import legend from "../utils/legend"

/*
 Polygons and Points
*/

const legendMap = {
  '#FFEB00': 'education',
  '#E60000': 'health',
  '#66CC00': 'environment',
  '#2482DB': 'volunteering',
  'darkgray': 'other',
}

const colorMap = {
  'education': '#FFEB00',
  'health': '#E60000',
  'environment': '#66CC00',
  'volunteering': '#2482DB',
  'other': 'darkgray',
};

const getColor = (scale, color, data, key) => {
  function getKeyWithHighestValue(obj) {
    const searchObj = _.omit(obj, ['totalProjects']);
    let maxKey = null;
    let maxValue = -Infinity;
    
    for (let key in searchObj) {
      if (searchObj[key] > maxValue) {
        maxKey = key;
        maxValue = searchObj[key];
      }
    }
    return searchObj['totalProjects'];
  }
  
  return scale(
    data.map(item => item.value[key] || item.value),
    color
  )
}

const getPolygonSelection = node => {
  return select(node).selectAll(".polygonPath")
}

const getPointSelection = node => {
  return select(node).selectAll(".pointCircleGroup")
}

const bindDataToSelection = (selection, data) => {
  selection
    .datum(() => null)
    .data(data, function(d) {
      return d ? d.id : this.id
    })
    .exit()
    .remove()
}

const updateFillOnSelection = (selection, color, key, exactDataKey) => {
  console.log(exactDataKey);
  console.log(key);
  selection.style("fill", d => {
    // return  d !== undefined ? color(d.value[key] || d.value) : "transparent"
    if (key !== 'totalProjects' && exactDataKey !== 'other') {
      if (d !== undefined) {
        if (d.value[key] > 0) {
          return colorMap[key];
        }
      }
    } else if (key === 'totalProjects' && exactDataKey === 'other') {
      if (d !== undefined) {
        if (d.value[key] > 0) {
          return 'darkgray';
        }
      }
      return "lightGray";
    } else {
      return "lightGray";
    }
  })
}

/*
 Tooltip
*/

const updateTooltip = (node, html) => {
  const [cursorX, cursorY] = mouse(node)

  const tooltip = select(node).select("#tooltipObject")
  const svg = select(node)

  tooltip
    .attr("x", cursorX + 10)
    .attr("y", cursorY + 10)
    .select("#tooltipDiv")
    .html(html)

  const { x: tooltipX } = tooltip.node().getBoundingClientRect()
  const { x: svgX, width: svgWidth } = svg.node().getBoundingClientRect()

  const tooltipWidth = tooltip.select("#tooltipDiv").node().scrollWidth

  if (tooltipX - svgX + tooltipWidth > svgWidth) {
    tooltip.attr("x", cursorX - tooltipWidth)
  }

  tooltip.select("#tooltipDiv").style("opacity", 1)
}

const removeTooltip = node => {
  select(node)
    .select("#tooltipDiv")
    .style("opacity", 0)
    .html("")
}

const attachTooltipToSelection = (node, selection, tooltip, key) => {
  // `this` is the element on which the mouse event is triggered
  // passed back to the tooltip function for customization options
  selection.on("mousemove", function(d) {
    updateTooltip(node, tooltip(d, this, key))
  })
  select(node).on("mouseout", () => removeTooltip(node))
}

/*
 Legend
*/

const onLegendMousemove = (node, classes, bounds, key, color, onLegendHover) => {
  const [lower, upper] = bounds;
  const legendKey = legendMap[color];
  
  getPolygonSelection(node).classed(
    classes.primaryPolygon,
    d => {
      const legendKey = legendMap[color];
      if (typeof onLegendHover === 'function') {
        onLegendHover(legendKey)
      }
      return true
    }
  )

  getPolygonSelection(node).classed(
    classes.highlightedPolygon,
    d => {
      console.log(lower, upper);
      if (typeof onLegendHover === 'function') {
        onLegendHover(legendKey);
      }
      return d.value[legendKey] && d.value[legendKey] > 0
    }
  )
  // updateFillOnSelection(getPolygonSelection(node), color, legendKey);
}

const onLegendMouseout = (node, classes, onLegendHover) => {
  getPolygonSelection(node).classed(classes.primaryPolygon, false);
  getPolygonSelection(node).classed(classes.highlightedPolygon, false);
  // updateFillOnSelection(getPolygonSelection(node), 'lightGray', 'totalProjects');
}

const updateLegend = (node, color, props, onLegendHover, legendHoverRef, legendSelectionRef) => {
  const { size, legend: legendProps, classes, dataKey } = props
  if (legendHoverRef) {
    legendHoverRef.current = {
      onLegendHover: () => (bounds, color) => onLegendMousemove(color),
      onLegendHoverOut: () => () => onLegendMouseout(node, classes),
      dataKey,
    }
  }

  legend({
    ...legendProps,
    node: node,
    color: color,
    tickFormat: ",.2r",
    width: size.width,
    marginLeft: size.width * 0.05,
    marginRight: size.width * 0.05,
    onMousemove: (bounds, color) => onLegendMousemove(node, classes, bounds, dataKey, color, onLegendHover),
    onMouseout: () => onLegendMouseout(node, classes, onLegendHover),
    onMouseclick: (bounds, color) => onLegendMousemove(node, classes, bounds, dataKey, color, onLegendHover),
  })
}

/*
 Click handler
*/

const attachClickHandlerToSelection = (selection, f) => {
  selection.on("click", function(d) {
    if (typeof f === "function") {
      f(d, )
    }
  })
}

export default function D3Container(props) {
  const { primaryMapData, pointMapData, dataKey, exactDataKey } = props
  const { size, scale, color, tooltip, onClick, onLegendHover, legendHoverRef } = props

  const ref = useRef()
  const legendSelectionRef = useRef(null);

  const [mapSvg] = useMemo(() => (ref.current ? ref.current.children : []), [
    ref.current,
  ])
  
  const mapColor = useMemo(
    () => {
      return getColor(scale, color, primaryMapData, dataKey)
    },
    [primaryMapData, dataKey, scale, color]
  )

  const isMounted = () => mapSvg && primaryMapData && primaryMapData.length

  useEffect(() => {
    const polygons = getPolygonSelection(mapSvg)
    if (isMounted) {
      bindDataToSelection(polygons, primaryMapData)
      attachClickHandlerToSelection(polygons, onClick)
    }
  }, [primaryMapData])

  useEffect(() => {
    isMounted &&
      updateFillOnSelection(getPolygonSelection(mapSvg), mapColor, dataKey, exactDataKey)
  }, [dataKey, mapColor])

  useEffect(() => {
    isMounted &&
      attachTooltipToSelection(
        mapSvg,
        getPolygonSelection(mapSvg),
        tooltip,
        dataKey
      )
  }, [primaryMapData, dataKey])

  useEffect(() => {
    if (mapSvg && pointMapData && pointMapData.length) {
      const points = getPointSelection(mapSvg)
      bindDataToSelection(points, pointMapData)
      attachTooltipToSelection(mapSvg, points, tooltip, dataKey)
    }
  }, [pointMapData])

  useEffect(() => {
    isMounted && updateLegend(mapSvg, mapColor, props, onLegendHover, legendHoverRef, legendSelectionRef)
  }, [mapSvg, primaryMapData, mapColor, size])

  return <div ref={ref}>{props.children}</div>
}
